// Drawer — generic right-side slide-in panel.
//
// Used by the EmailComposer (phase 9) and the AiHelpPanel (phase
// 10) for "compose while looking at the record" flows. Mirrors the
// Modal API but lays out as a right-rail panel instead of a
// centered card.
//
// Behaviors:
//   * Escape key closes
//   * Click outside (on the backdrop) closes
//   * Focus trap: tabbing cycles within the drawer
//   * Body scroll locked while open
//   * Optional `onClose` always fires when the drawer is dismissed
//   * `width` prop controls the rail width (default 480px)

import {
  useCallback,
  useEffect,
  useRef,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  /** The dialog's accessible name when it draws its own header (no
   *  title). Falls back to the title. */
  ariaLabel?: string;
  /** Extra class on the panel, for a drawer that styles its own surface. */
  panelClassName?: string;
  width?: number;
  children: ReactNode;
  // Optional footer rendered at the bottom of the drawer (e.g.
  // Send / Cancel buttons).
  footer?: ReactNode;
}

export function Drawer({
  open,
  onClose,
  title,
  ariaLabel,
  panelClassName,
  width = 480,
  children,
  footer,
}: DrawerProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Body scroll lock
  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  // Escape closes
  useEffect(() => {
    if (!open) return;
    const handler = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Focus the panel on open so keyboard navigation works immediately
  useEffect(() => {
    if (open && panelRef.current) {
      panelRef.current.focus();
    }
  }, [open]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      // Tab cycle within the panel (cheap focus-trap — full focus
      // cycle from first to last focusable would require a more
      // elaborate implementation; this is good enough for the
      // composer use-case).
      if (e.key !== "Tab") return;
      if (!panelRef.current) return;
      const focusable = panelRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex="0"], [contenteditable="true"]',
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [],
  );

  if (!open) return null;
  // Render via createPortal to document.body so the drawer escapes any
  // parent stacking context (e.g. the sidebar, which is fixed-positioned
  // and creates its own context — and so its child z-index never beats
  // dashboard cards even though .drawer-backdrop is z-index 2000).
  // Without the portal, the EmailComposer drawer renders nested inside
  // <Sidebar><EmailSidebarSection><li><EmailComposer/>, and the local
  // z-index is meaningless against absolutely-positioned siblings of
  // the sidebar that live in the document root. Portaling out solves
  // this for every drawer use site (composer, AI panel, schedule send).
  // Workers / Vitest / SSR safety: render inline if document is missing.
  const tree = (
    <div
      className="drawer-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="presentation"
    >
      <div
        ref={panelRef}
        className={panelClassName ? `drawer-panel ${panelClassName}` : "drawer-panel"}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel ?? title}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        style={{ width, maxWidth: "calc(100vw - 24px)" }}
      >
        {title ? (
          <header className="drawer-header">
            <h2>{title}</h2>
            <button
              type="button"
              className="drawer-close"
              onClick={onClose}
              aria-label="Close drawer"
            >
              <X size={16} strokeWidth={1.5} aria-hidden />
            </button>
          </header>
        ) : null}
        <div className="drawer-body">{children}</div>
        {footer ? <footer className="drawer-footer">{footer}</footer> : null}
      </div>
    </div>
  );
  if (typeof document === "undefined") return tree;
  return createPortal(tree, document.body);
}
